import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { History, RotateCcw, Loader2, Clock, ChevronDown, ChevronUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VersionHistory({ projectId, currentVersion, onRestore }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [restoring, setRestoring] = useState(null);

  useEffect(() => {
    if (!expanded) return;
    setLoading(true);
    axios.get(`${API}/projects/${projectId}/versions`, { withCredentials: true })
      .then(res => setVersions(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [projectId, expanded, currentVersion]);

  const handleRestore = async (versionNum) => {
    if (!window.confirm(`Version ${versionNum} wiederherstellen? Die aktuelle Version wird als Backup gespeichert.`)) return;
    setRestoring(versionNum);
    try {
      await axios.post(`${API}/projects/${projectId}/restore/${versionNum}`, {}, { withCredentials: true });
      onRestore();
    } catch (err) {
      console.error(err);
    }
    setRestoring(null);
  };

  return (
    <div className="glass-surface rounded-lg overflow-hidden">
      {/* Header - Toggle */}
      <button
        data-testid="version-history-toggle"
        onClick={() => setExpanded(p => !p)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.02] transition-colors"
      >
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-[#00F0FF]" />
          <span className="font-['Outfit'] text-sm font-medium text-white">Versionshistorie</span>
          <span className="text-[10px] font-['JetBrains_Mono'] text-[#606070]">v{currentVersion}</span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-[#606070]" /> : <ChevronDown className="w-4 h-4 text-[#606070]" />}
      </button>

      {/* Content */}
      {expanded && (
        <div className="border-t border-white/5 px-4 py-3">
          {loading && (
            <div className="flex justify-center py-4">
              <Loader2 className="w-4 h-4 text-[#00F0FF] animate-spin" />
            </div>
          )}

          {!loading && versions.length === 0 && (
            <p className="text-xs text-[#606070] text-center py-3 font-['JetBrains_Mono']">
              Keine frueheren Versionen vorhanden
            </p>
          )}

          {!loading && versions.length > 0 && (
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {/* Current version indicator */}
              <div className="flex items-center gap-3 px-3 py-2 rounded bg-[#00F0FF]/5 border border-[#00F0FF]/15">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#00F0FF]/20 flex-shrink-0">
                  <span className="text-[10px] font-['JetBrains_Mono'] text-[#00F0FF] font-bold">v{currentVersion}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-xs text-[#00F0FF] font-medium">Aktuelle Version</span>
                </div>
              </div>

              {versions.map((v) => (
                <div key={v.id} className="flex items-center gap-3 px-3 py-2 rounded bg-white/[0.02] border border-white/5 hover:border-white/10 transition-colors group">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-white/5 flex-shrink-0">
                    <span className="text-[10px] font-['JetBrains_Mono'] text-[#A0A0B0]">v{v.version}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-white block truncate">{v.projectTitle}</span>
                    <span className="text-[10px] text-[#606070] font-['JetBrains_Mono'] flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(v.created_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                      {v.analysis && ` | ${v.analysis.projectComplexity}`}
                    </span>
                  </div>
                  <button
                    data-testid={`restore-version-${v.version}`}
                    onClick={() => handleRestore(v.version)}
                    disabled={restoring === v.version}
                    className="flex items-center gap-1 px-2 py-1 text-[10px] bg-white/5 border border-white/10 text-[#A0A0B0] hover:text-[#00F0FF] hover:border-[#00F0FF]/30 rounded transition-all opacity-0 group-hover:opacity-100 flex-shrink-0"
                  >
                    {restoring === v.version ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                    Wiederherstellen
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
